<template>
	<div
		v-editable="blok"
		class="w-full grid gap-4 grid-cols-1 static-categories"
	>
		<span class="section-title text-3xl mb-4" :class="blok.title_align || ''">{{
			blok.title
		}}</span>

		<template v-if="blok.is_carousel">
			<Carousel
				v-if="hasCategoryTree"
				:slides="carouselSlides"
				:blok="blok"
				:type-of-slide="ItemType.CATEGORY"
				:aligning="blok.aligning"
			/>
		</template>
		<template v-else-if="isLogoRow">
			<div
				class="static-categories-logo-row flex flex-wrap items-center justify-center gap-x-12 gap-y-8"
			>
				<StaticCategoriesLogoItem
					v-for="item in logoItems"
					:key="item._uid"
					:link="item.link"
					:name="item.name"
					:image="item.image"
				/>
			</div>
		</template>
		<template v-else>
			<div :class="listClasses">
				<div v-for="item in heroItems" :key="item._uid" :class="itemClasses">
					<CategoryHero
						:blok="item"
						:lazy-load-images="blok.lazy_load_images"
						:sizes="tileSizes"
					/>
				</div>
			</div>
		</template>

		<div v-if="blok.call_to_action?.length > 0" class="grid justify-center">
			<StoryblokComponent
				v-for="call_to_action in blok.call_to_action"
				:key="call_to_action._uid"
				:blok="call_to_action"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
// `static-categories`, `static-categories-scroll` and
// `static-categories-logo-row` are styling hooks (styled in sportkost).
import type {
	StaticCategoriesBlok,
	CategoryHeroBlok
} from "~/types/storyblok/storyblok";
import type { CategoryWithFiles } from "@nwt/norce-pwa/dist/runtime/types/api/category/category";
import { categoryTileSizes } from "~/layers/base/utils/categoryTileSizes";
import { categoryHeroId } from "~/utils/storyblok/categoryHeroId";

interface Props {
	blok: StaticCategoriesBlok;
}
const { blok } = defineProps<Props>();

const hasCategoryTree = useHasCategoryTree();

const { resolveLink } = useStoryblokLink();

// The block's tile settings apply to every tile that does not set its own.
const aspectRatioOf = (item: CategoryHeroBlok) =>
	item.aspect_ratio || blok.aspect_ratio;
const buttonTextOf = (item: CategoryHeroBlok) =>
	item.button_text || blok.button_text;

// The raw `link` and category fields stay on the item: CategoryHero resolves
// them once.
const toCategoryHeroBlok = (
	item: CategoryHeroBlok
): CategoryHeroBlok & { aspectRatio: string | null } => ({
	...item,
	text_below: item.text_below || blok.text_below,
	aspect_ratio: aspectRatioOf(item),
	button_text: buttonTextOf(item),
	aspectRatio: aspectRatioOf(item) || null
});

// Only the fields the carousel's category card reads, not a full Norce category.
const toCategoryWithFiles = (item: CategoryHeroBlok): CategoryWithFiles =>
	({
		Id: Number(categoryHeroId(item)),
		CategoryId: Number(categoryHeroId(item)),
		Name: item.name,
		Files: [{ ImagePath: item.image?.filename }],
		aspectRatio: aspectRatioOf(item) || null,
		buttonText: buttonTextOf(item)
	}) as unknown as CategoryWithFiles;

const carouselSlides = blok.is_carousel
	? (blok.items?.map(toCategoryWithFiles) ?? [])
	: [];
const heroItems = blok.is_carousel
	? []
	: (blok.items?.map(toCategoryHeroBlok) ?? []);

const isLogoRow = computed(() => blok.items_in_row === "auto-width");

// Logo items need a resolved href. Resolved here at setup rather than in a
// computed, because the category fallback reads the locale and the category
// store, which must not run lazily. Only built for the logo row, so grid tiles
// (which resolve inside CategoryHero) are not resolved twice.
const logoItems =
	!blok.is_carousel && isLogoRow.value
		? heroItems.map((item) => ({
				_uid: item._uid,
				link: resolveLink(item.link, categoryHeroId(item)),
				name: item.name,
				image: item.image?.filename
			}))
		: [];

const listClasses = computed(() =>
	blok.scrollable
		? "flex gap-4 overflow-x-auto no-scrollbar static-categories-scroll"
		: `grid gap-4 justify-between ${blok.items_in_row || "md:grid-cols-4 grid-cols-2"}`
);

const itemClasses = computed(() =>
	blok.scrollable ? "shrink-0 w-32 sm:w-36 md:w-auto md:flex-1 md:min-w-0" : ""
);

// Tile `sizes` follow the same layout fields as the classes above.
const tileSizes = computed(() =>
	categoryTileSizes({
		itemsInRow: blok.items_in_row,
		scrollable: blok.scrollable,
		itemCount: heroItems.length
	})
);
</script>
